import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Loader2, PackagePlus, Search, Undo2 } from 'lucide-react';
import apiClient from '../../api/client';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';
import type { RowResolution, StockPreviewRow } from './types';

interface SearchHit {
  id: string;
  sku: string;
  name: string;
  brand: string | null;
  manufacturerRef: string | null;
  active: boolean;
}

const chip =
  'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] hover:bg-accent disabled:opacity-50';

/**
 * Step 3, one line the file could not settle on its own (2026-10-13): link it to a product we
 * have, create the product (from the TecDoc brand behind the number, or by hand), or — when
 * several products answer to the reference — pick the right one. Every decision re-runs the
 * preview on the server, so what is shown is what the commit will do.
 */
export function ResolveLine({
  row,
  canCreate,
  disabled,
  onResolve,
}: {
  row: StockPreviewRow;
  canCreate: boolean;
  disabled: boolean;
  onResolve: (line: number, decision: RowResolution | null) => void;
}) {
  const { t } = useTranslation();
  const [panel, setPanel] = useState<null | 'link' | 'create'>(null);

  if (row.resolvedBy === 'OPERATOR') {
    return (
      <button
        type="button"
        className={cn(chip, 'mt-1')}
        disabled={disabled}
        onClick={() => onResolve(row.line, null)}
      >
        <Undo2 className="h-3 w-3" /> {t('import.stock.resolve.undo')}
      </button>
    );
  }

  const ambiguous = row.status === 'ERROR' && (row.candidates?.length ?? 0) > 0;
  const open = row.status === 'NOT_FOUND' || ambiguous || row.status === 'CREATE';
  if (!open) return null;

  const tecdocChoices = row.status === 'NOT_FOUND' && canCreate ? (row.tecdoc ?? []) : [];
  const createFrom = (brand: string | null, articleNr: string | null, artId: number | null) =>
    onResolve(row.line, {
      action: 'CREATE',
      name: row.description || row.ref,
      brand,
      manufacturerRef: articleNr || row.ref,
      purchasePriceHT: row.unitPrice ?? null,
      tecdocArtId: artId,
    });

  return (
    <div className="mt-1 space-y-1">
      <div className="flex flex-wrap gap-1">
        {ambiguous &&
          row.candidates!.map((c) => (
            <button
              key={c.id}
              type="button"
              className={chip}
              disabled={disabled}
              onClick={() => onResolve(row.line, { action: 'LINK', productId: c.id })}
              title={c.name}
            >
              <Link2 className="h-3 w-3" /> {c.sku}
              {c.brand ? ` · ${c.brand}` : ''}
            </button>
          ))}
        {tecdocChoices.map((c) => (
          <button
            key={c.artId}
            type="button"
            className={chip}
            disabled={disabled}
            onClick={() => createFrom(c.brand, c.articleNr, c.artId)}
          >
            <PackagePlus className="h-3 w-3" />{' '}
            {t('import.stock.resolve.createFromTecdoc', { brand: c.brand })}
          </button>
        ))}
        <button
          type="button"
          className={cn(chip, panel === 'link' && 'border-primary')}
          disabled={disabled}
          onClick={() => setPanel(panel === 'link' ? null : 'link')}
        >
          <Search className="h-3 w-3" />{' '}
          {row.status === 'CREATE'
            ? t('import.stock.resolve.linkInstead')
            : t('import.stock.resolve.link')}
        </button>
        {canCreate && row.status !== 'CREATE' && (
          <button
            type="button"
            className={cn(chip, panel === 'create' && 'border-primary')}
            disabled={disabled}
            onClick={() => setPanel(panel === 'create' ? null : 'create')}
          >
            <PackagePlus className="h-3 w-3" /> {t('import.stock.resolve.create')}
          </button>
        )}
      </div>
      {panel === 'link' && (
        <LinkSearch
          initial={row.ref}
          disabled={disabled}
          onPick={(id) => {
            setPanel(null);
            onResolve(row.line, { action: 'LINK', productId: id });
          }}
        />
      )}
      {panel === 'create' && (
        <CreateForm
          row={row}
          disabled={disabled}
          onSave={(d) => {
            setPanel(null);
            onResolve(row.line, d);
          }}
        />
      )}
    </div>
  );
}

function LinkSearch({
  initial,
  disabled,
  onPick,
}: {
  initial: string;
  disabled: boolean;
  onPick: (productId: string) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState(initial);
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setHits(null);
      return;
    }
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      apiClient
        .get<SearchHit[]>('/purchasing/stock-import/products', { params: { q: term } })
        .then((r) => alive && setHits(r.data))
        .catch(() => alive && setHits([]))
        .finally(() => alive && setLoading(false));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q]);

  return (
    <div className="w-[22rem] max-w-full rounded border bg-background p-1.5">
      <div className="flex items-center gap-1">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('import.stock.resolve.searchPlaceholder')}
          className="h-7 w-full rounded border px-2 text-xs"
        />
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>
      {hits && (
        <ul className="mt-1 max-h-40 overflow-auto text-xs">
          {hits.length === 0 && (
            <li className="px-1 py-1 text-muted-foreground">{t('import.stock.resolve.noHits')}</li>
          )}
          {hits.map((h) => (
            <li key={h.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onPick(h.id)}
                className="flex w-full items-baseline gap-2 rounded px-1 py-1 text-left hover:bg-accent"
              >
                <span className="font-mono">{h.sku}</span>
                <span className="truncate text-muted-foreground">
                  {[h.brand, h.name].filter(Boolean).join(' · ')}
                </span>
                {!h.active && (
                  <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                    {t('import.stock.resolve.inactive')}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateForm({
  row,
  disabled,
  onSave,
}: {
  row: StockPreviewRow;
  disabled: boolean;
  onSave: (d: RowResolution) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(row.description || row.ref);
  const [brand, setBrand] = useState(row.fileBrand ?? row.tecdoc?.[0]?.brand ?? '');
  const [ref, setRef] = useState(row.ref);
  const [price, setPrice] = useState(row.unitPrice != null ? String(row.unitPrice) : '');
  const priceNum = price.trim() === '' ? null : Number(price.replace(',', '.'));
  const valid = name.trim() !== '' && ref.trim() !== '' && (priceNum == null || priceNum >= 0);
  const input = 'h-7 w-full rounded border px-2 text-xs';
  return (
    <div className="grid w-[22rem] max-w-full grid-cols-2 gap-1.5 rounded border bg-background p-1.5 text-xs">
      <label className="col-span-2">
        <span className="text-muted-foreground">{t('import.stock.resolve.name')}</span>
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        <span className="text-muted-foreground">{t('import.stock.resolve.reference')}</span>
        <input className={input} value={ref} onChange={(e) => setRef(e.target.value)} />
      </label>
      <label>
        <span className="text-muted-foreground">{t('import.stock.resolve.brand')}</span>
        <input className={input} value={brand} onChange={(e) => setBrand(e.target.value)} />
      </label>
      <label>
        <span className="text-muted-foreground">{t('import.stock.resolve.price')}</span>
        <input
          className={input}
          inputMode="decimal"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
      </label>
      <div className="flex items-end justify-end">
        <Button
          size="sm"
          disabled={disabled || !valid || Number.isNaN(priceNum)}
          onClick={() =>
            onSave({
              action: 'CREATE',
              name: name.trim(),
              brand: brand.trim() || null,
              manufacturerRef: ref.trim(),
              purchasePriceHT: priceNum,
            })
          }
        >
          {t('import.stock.resolve.save')}
        </Button>
      </div>
    </div>
  );
}
